import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Scan, Package, MapPin, CheckCircle, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

/**
 * Slice D8 — Per-section FG Pick (manual) clone (Salem-untouched).
 *
 * Operator scans staff → scans pack barcode → confirms pick. Inverse of
 * D7 FG Put-Away. Calls /api/per-section/{slug}/fg-pick endpoints.
 */

const SLUG_TO_DISPLAY = {
  color_combo: 'Color Combo Section',
  art_silk: 'Art Silk Section',
  kids: 'Kids Section',
};

const PerSectionFgPickClone = () => {
  const navigate = useNavigate();
  const { section: slug } = useParams();
  const sectionName = SLUG_TO_DISPLAY[slug] || slug;
  const backRoute = `/sections/dispatch/${slug}`;
  const apiBase = `/per-section/${slug}/fg-pick`;

  const staffInputRef = useRef(null);
  const packInputRef = useRef(null);

  const [staffId, setStaffId] = useState('');
  const [staffValidated, setStaffValidated] = useState(false);
  const [staffName, setStaffName] = useState('');

  const [packBarcode, setPackBarcode] = useState('');
  const [packDetails, setPackDetails] = useState(null);
  const [binDetails, setBinDetails] = useState(null);

  const [pickSlipNo, setPickSlipNo] = useState('');
  const [remarks, setRemarks] = useState('');

  const [loading, setLoading] = useState(false);
  const [pickSuccess, setPickSuccess] = useState(false);

  useEffect(() => {
    staffInputRef.current?.focus();
  }, []);

  const handleStaffValidate = async () => {
    if (!staffId.trim()) {
      toast.error('Please enter Staff ID');
      return;
    }
    try {
      setLoading(true);
      const response = await apiClient.get(`/validate/staff/${staffId}`);
      if (response.data.valid) {
        setStaffValidated(true);
        setStaffName(response.data.staff_name);
        toast.success(`Staff validated: ${response.data.staff_name}`);
        setTimeout(() => packInputRef.current?.focus(), 100);
      } else {
        toast.error('Invalid Staff ID');
      }
    } catch (error) {
      toast.error('Staff validation failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePackScan = async () => {
    if (!packBarcode.trim()) {
      toast.error('Please scan or enter pack barcode');
      return;
    }
    try {
      setLoading(true);
      setPickSuccess(false);
      const response = await apiClient.post(
        `${apiBase}/validate-pack/${encodeURIComponent(packBarcode)}`
      );
      setPackDetails(response.data.pack);
      setBinDetails(response.data.bin);
      toast.success(
        response.data.pack.bin_code
          ? `Pack found in bin ${response.data.pack.bin_code}`
          : 'Pack validated for pick'
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Pack validation failed');
      setPackDetails(null);
      setBinDetails(null);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPick = async () => {
    if (!packDetails) {
      toast.error('Please scan a pack first');
      return;
    }
    try {
      setLoading(true);
      const response = await apiClient.post(apiBase, {
        staff_id: staffId,
        pack_barcode: packBarcode,
        pick_slip_no: pickSlipNo || undefined,
        remarks: remarks || undefined,
      });
      toast.success(response.data.message);
      setPickSuccess(true);
      setTimeout(() => {
        setPackBarcode('');
        setPackDetails(null);
        setBinDetails(null);
        setRemarks('');
        setPickSuccess(false);
        packInputRef.current?.focus();
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Pick failed');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter') action();
  };

  const resetAll = () => {
    setStaffId('');
    setStaffValidated(false);
    setStaffName('');
    setPackBarcode('');
    setPackDetails(null);
    setBinDetails(null);
    setPickSlipNo('');
    setRemarks('');
    setPickSuccess(false);
    staffInputRef.current?.focus();
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid={`per-section-fg-pick-${slug}`}>
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center">
          <Button
            variant="ghost"
            onClick={() => navigate(backRoute)}
            className="text-[#F9FAFB] hover:bg-white/10 mr-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold font-display">FG Pick</h1>
            <p className="text-sm text-[#E8DCC8]">{sectionName}</p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 md:px-6 py-8">
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 mb-6">
          <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4">Staff Validation</h2>
          {!staffValidated ? (
            <div className="flex gap-3">
              <div className="flex-1">
                <Label>Staff ID</Label>
                <Input
                  ref={staffInputRef}
                  value={staffId}
                  onChange={(e) => setStaffId(e.target.value)}
                  onKeyPress={(e) => handleKeyPress(e, handleStaffValidate)}
                  placeholder="Scan or enter Staff ID"
                  className="mt-1"
                  data-testid="staff-id-input"
                />
              </div>
              <div className="flex items-end">
                <Button
                  onClick={handleStaffValidate}
                  disabled={loading}
                  className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                  data-testid="validate-staff-btn"
                >
                  Validate
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-medium text-green-800">{staffName}</span>
                <span className="text-sm text-green-600">({staffId})</span>
              </div>
              <Button variant="ghost" size="sm" onClick={resetAll} data-testid="change-staff-btn">
                Change
              </Button>
            </div>
          )}
        </div>

        {staffValidated && (
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 mb-6">
            <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4">
              <Scan className="w-5 h-5 inline mr-2" />
              Scan Pack to Pick
            </h2>
            <div className="flex gap-3 mb-4">
              <div className="flex-1">
                <Label>Pack Barcode (USN)</Label>
                <Input
                  ref={packInputRef}
                  value={packBarcode}
                  onChange={(e) => setPackBarcode(e.target.value)}
                  onKeyPress={(e) => handleKeyPress(e, handlePackScan)}
                  placeholder="Scan pack barcode"
                  className="mt-1"
                  data-testid="pack-barcode-input"
                />
              </div>
              <div className="flex items-end">
                <Button
                  onClick={handlePackScan}
                  disabled={loading || !packBarcode}
                  className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                  data-testid="validate-pack-btn"
                >
                  Validate
                </Button>
              </div>
            </div>

            {packDetails && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <h3 className="font-medium text-[#1A1F2E] mb-2">Pack Details</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-[#5A5F6E]">Pack:</span>
                    <span className="ml-2 font-medium">{packDetails.pack_barcode}</span>
                  </div>
                  <div>
                    <span className="text-[#5A5F6E]">SKU:</span>
                    <span className="ml-2 font-medium">{packDetails.sku_code || '—'}</span>
                  </div>
                  <div>
                    <span className="text-[#5A5F6E]">Bundle:</span>
                    <span className="ml-2 font-medium">{packDetails.bundle_code || '—'}</span>
                  </div>
                  <div>
                    <span className="text-[#5A5F6E]">Size:</span>
                    <span className="ml-2 font-medium">{packDetails.size || '—'}</span>
                  </div>
                  {packDetails.design_name && (
                    <div className="col-span-2">
                      <span className="text-[#5A5F6E]">Design:</span>
                      <span className="ml-2 font-medium">{packDetails.design_name}</span>
                    </div>
                  )}
                </div>
                {packDetails.bin_code && (
                  <div className="mt-3 flex items-center gap-2 text-sm bg-amber-50 p-2 rounded border border-amber-200">
                    <MapPin className="w-4 h-4 text-amber-600" />
                    <span className="text-amber-900">
                      Pick from bin <strong>{packDetails.bin_code}</strong>
                      {binDetails && binDetails.current_qty != null && (
                        <span className="ml-2 text-xs text-amber-700">
                          ({binDetails.current_qty} packs in bin)
                        </span>
                      )}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {packDetails && (
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 mb-6">
            <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4">
              <Package className="w-5 h-5 inline mr-2" />
              Pick Details (Optional)
            </h2>
            <div className="space-y-4">
              <div>
                <Label>Pick Slip Number</Label>
                <Input
                  value={pickSlipNo}
                  onChange={(e) => setPickSlipNo(e.target.value)}
                  placeholder="e.g. FGS-2026-0042 (optional)"
                  data-testid="pick-slip-input"
                />
              </div>
              <div>
                <Label>Remarks</Label>
                <Textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Add any pick notes..."
                  rows={2}
                  data-testid="remarks-input"
                />
              </div>
            </div>
          </div>
        )}

        {packDetails && (
          <Button
            onClick={handleConfirmPick}
            disabled={loading || pickSuccess}
            className={`w-full h-14 text-lg ${
              pickSuccess ? 'bg-green-500 hover:bg-green-500' : 'bg-[#2D5F4F] hover:bg-[#234A3D]'
            } text-white`}
            data-testid="pick-btn"
          >
            {pickSuccess ? (
              <>
                <CheckCircle className="w-6 h-6 mr-2" />
                Pick Successful!
              </>
            ) : (
              <>
                <Truck className="w-6 h-6 mr-2" />
                Confirm Pick
              </>
            )}
          </Button>
        )}
      </main>
    </div>
  );
};

export default PerSectionFgPickClone;
